import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDocuments } from './contexts/DocumentsContext';
import {
    Layout,
    BookOpen,
    Settings,
    Search,
    Menu,
    Plus,
    FileText,
    GraduationCap,
    Clock,
    Grid,
    List,
    MoreVertical,
    Trash2,
    Upload,
    LogOut,
    ChevronLeft,
    ChevronRight,
    User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserButton, useUser } from "@clerk/clerk-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const { documents, loading, refreshDocuments } = useDocuments();
    const [uploading, setUploading] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [isCollapsed, setIsCollapsed] = useState(false);
    const fileInputRef = useRef(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };



    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        if (user) {
            formData.append('user_id', user.id);
        }

        setUploading(true);
        try {
            await axios.post(`${API}/documents/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Document uploaded successfully!');
            await refreshDocuments();
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (documentId) => {
        try {
            await axios.delete(`${API}/documents/${documentId}`);
            toast.success('Document deleted');
            refreshDocuments();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete document');
        }
    };

    const handleLogout = () => {
        // Add any logout logic here (clearing tokens, etc.)
        toast.success('Logged out successfully');
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-slate-50 flex font-['Outfit',sans-serif]">
            <Toaster position="top-center" />

            {/* Sidebar */}
            <aside
                className={`${isCollapsed ? 'w-24' : 'w-64'
                    } bg-slate-900 text-white hidden md:flex flex-col fixed h-full z-20 transition-all duration-300 ease-in-out`}
            >
                <div className={`p-6 flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
                    <img src="/logo2.png" alt="Axora" className="w-14 h-14 object-contain" />
                    {!isCollapsed && <span className="text-xl font-bold tracking-tight">Axora</span>}
                </div>

                <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto custom-scrollbar">
                    <div className={`text-xs font-semibold text-slate-500 mb-4 px-2 ${isCollapsed ? 'hidden' : 'block'}`}>
                        YOUR DOCUMENTS
                    </div>
                    {documents.map((doc) => (
                        <Button
                            key={doc.id}
                            variant="ghost"
                            onClick={() => navigate(`/chat/${doc.id}`)}
                            className={`w-full ${isCollapsed ? 'justify-center px-0' : 'justify-start'} text-slate-300 hover:text-white hover:bg-white/10 transition-colors truncate`}
                            title={doc.filename}
                        >
                            <FileText className="w-5 h-5 flex-shrink-0" />
                            {!isCollapsed && <span className="ml-3 truncate">{doc.filename}</span>}
                        </Button>
                    ))}
                    {documents.length === 0 && !isCollapsed && (
                        <div className="px-2 text-sm text-slate-500">No documents yet</div>
                    )}
                </nav>

                <div className="p-4 border-t border-slate-800 flex justify-end">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="text-slate-400 hover:text-white hover:bg-white/10"
                    >
                        {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                    </Button>
                </div>
            </aside>

            {/* Main Content */}
            <main className={`flex-1 ${isCollapsed ? 'md:ml-24' : 'md:ml-64'} min-h-screen flex flex-col transition-all duration-300 ease-in-out`}>
                {/* Header */}
                <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-10">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative w-full max-w-md hidden md:block">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Search documents..."
                                className="pl-10 bg-slate-50 border-slate-200 focus:bg-white transition-colors"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <Button className="md:hidden" variant="ghost" size="icon">
                            <Menu className="w-6 h-6" />
                        </Button>

                        {/* User Profile Dropdown */}
                        <div className="flex items-center gap-2">
                            <UserButton afterSignOutUrl="/" />
                        </div>
                    </div>
                </header>

                <div className="p-6 max-w-7xl mx-auto w-full space-y-8">
                    {/* Welcome & Stats */}
                    <div className="space-y-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Welcome back, {user?.firstName || 'User'}!</h1>
                                <p className="text-slate-500">Here's what's happening with your study materials.</p>
                            </div>
                            <div>
                                <Button
                                    onClick={handleUploadClick}
                                    className="bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-200 transition-all hover:-translate-y-0.5"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Upload New
                                </Button>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleFileUpload}
                                accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg"
                                disabled={uploading}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="border-slate-200 shadow-sm">
                                <CardContent className="p-6 flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-slate-500">Total Documents</p>
                                        <p className="text-2xl font-bold text-slate-900">{documents.length}</p>
                                    </div>
                                </CardContent>
                            </Card>


                        </div>
                    </div>

                    {/* Recent Documents */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-bold text-slate-900">Your Library</h2>
                            <div className="flex bg-slate-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <Grid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {uploading && (
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-3 text-blue-700 animate-pulse">
                                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                Uploading your document...
                            </div>
                        )}

                        {loading ? (
                            <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-3"}>
                                {[...Array(6)].map((_, i) => (
                                    viewMode === 'grid' ? (
                                        <Card key={i} className="border-slate-200 shadow-sm">
                                            <Skeleton className="h-32 rounded-t-xl" />
                                            <CardContent className="p-4 space-y-3">
                                                <Skeleton className="h-5 w-3/4" />
                                                <Skeleton className="h-3 w-1/2" />
                                                <div className="flex gap-2 pt-2">
                                                    <Skeleton className="h-8 flex-1" />
                                                    <Skeleton className="h-8 flex-1" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ) : (
                                        <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4">
                                            <Skeleton className="w-10 h-10 rounded-lg" />
                                            <div className="flex-1 space-y-2">
                                                <Skeleton className="h-4 w-1/2" />
                                                <Skeleton className="h-3 w-1/3" />
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        ) : documents.length === 0 && !uploading ? (
                            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Upload className="w-8 h-8 text-slate-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-1">No documents yet</h3>
                                <p className="text-slate-500 max-w-xs mx-auto mb-6">Upload your first PDF or note to get started with AI-powered studying.</p>
                                <Button
                                    variant="outline"
                                    className="cursor-pointer"
                                    onClick={handleUploadClick}
                                >
                                    Select File
                                </Button>
                            </div>
                        ) : (
                            <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-3"}>
                                {documents.map((doc) => (
                                    viewMode === 'grid' ? (
                                        <Card
                                            key={doc.id}
                                            className="group hover:shadow-md transition-all border-slate-200 cursor-pointer overflow-hidden"
                                            onClick={() => navigate(`/chat/${doc.id}`)}
                                        >
                                            <div className="h-32 bg-slate-100 relative flex items-center justify-center group-hover:bg-slate-50 transition-colors">
                                                <FileText className="w-12 h-12 text-slate-300 group-hover:scale-110 transition-transform duration-300" />
                                                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 bg-white/80 hover:bg-white text-red-500 hover:text-red-600 shadow-sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(doc.id);
                                                        }}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <CardContent className="p-4">
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <h3 className="font-semibold text-slate-900 truncate" title={doc.filename}>{doc.filename}</h3>
                                                    <MoreVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                </div>
                                                <p className="text-xs text-slate-500 mb-4 flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {new Date(doc.upload_date).toLocaleDateString()}
                                                </p>
                                                <div className="flex gap-2">
                                                    <Button size="sm" className="flex-1 bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-100 shadow-none">
                                                        Chat
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="flex-1"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/tools/${doc.id}`);
                                                        }}
                                                    >
                                                        Quiz
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ) : (
                                        <div
                                            key={doc.id}
                                            className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-sm transition-shadow cursor-pointer group"
                                            onClick={() => navigate(`/chat/${doc.id}`)}
                                        >
                                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500">
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-medium text-slate-900 truncate">{doc.filename}</h3>
                                                <p className="text-xs text-slate-500">Uploaded on {new Date(doc.upload_date).toLocaleDateString()}</p>
                                            </div>
                                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button size="sm" variant="ghost">Chat</Button>
                                                <Button size="sm" variant="ghost" onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/tools/${doc.id}`);
                                                }}>Tools</Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(doc.id);
                                                    }}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
